import useTranslation from 'next-translate/useTranslation';
import { useCallback, useMemo } from 'react';

import { FEATURE } from '~/common/enums/feature.enum';
import type { PaymentMethod } from '~/common/enums/payments.enum';
import {
  FeeType,
  PAYMENT_TYPE_TO_METHOD,
  PaymentType,
  TransactionType,
} from '~/common/enums/payments.enum';
import { formatDollars } from '~/domains/common/utils/formatters';
import { useIsFeatureEnabled } from '~/hooks/useIsFeatureEnabled';

import { DEPOSIT_ONLY_PAYMENT_TYPES } from '../../constants';
import { useAvailablePaymentMethods } from '../../hooks/useAvailablePaymentMethods';
import { useProcessingFees } from '../../hooks/useProcessingFees';

export type PaymentTypeSettings = { isDisabled?: boolean; disabledReason?: string } & (
  | {
      isFree: true;
    }
  | {
      isFree: false;
      description: string;
    }
);

type PaymentTypesSettingsMap = {
  [K in PaymentType]: PaymentTypeSettings;
};

type PaymentTypes = [PaymentType, PaymentTypeSettings][];

export function useWithdrawalPaymentMethods({ paymentType }: { paymentType: PaymentType }) {
  const { t } = useTranslation('payments');

  const showVIPPAndCheck = useIsFeatureEnabled(FEATURE.SHOW_VIPP_AND_PAPER_CHECK);
  const showVenmo = useIsFeatureEnabled(FEATURE.ENABLE_VENMO);

  const { availablePaymentMethods, isLoadingAvailablePaymentMethods } = useAvailablePaymentMethods({
    transactionType: TransactionType.WITHDRAWAL,
  });

  const isPaymentMethodDisplayed = useCallback(
    (method: PaymentMethod) => {
      const availableMethod = availablePaymentMethods?.some(
        (item) => item.paymentMethod === method && item.status === 'displayed'
      );

      return availableMethod;
    },
    [availablePaymentMethods]
  );

  const { processingFees, isLoading: isProcessingFeesLoading } = useProcessingFees({
    paymentMethod: PAYMENT_TYPE_TO_METHOD[paymentType],
    transactionType: TransactionType.WITHDRAWAL,
  });

  const withdrawalProcessingFees = useMemo(
    () => processingFees?.filter((fee) => fee.transactionType === TransactionType.WITHDRAWAL),
    [processingFees]
  );

  const getFee = useCallback(
    (type: PaymentType) => {
      const fee = withdrawalProcessingFees?.find(
        (item) => item.paymentMethod === PAYMENT_TYPE_TO_METHOD[type]
      );

      if (!fee) {
        return 0;
      }
      if (fee.type === FeeType.AMOUNT) {
        return formatDollars(fee.amount);
      }
      if (fee.type === FeeType.PERCENTAGE) {
        return `${fee.amount}%`;
      }

      return fee?.type;
    },
    [withdrawalProcessingFees]
  );

  const createPaymentTypeSetting = useCallback(
    (type: PaymentType) => {
      type DescriptionKey = Parameters<typeof t>[0];

      const defaultSetting = {
        isFree: true,
        descriptionKey: 'withdrawal.descriptions.default' satisfies DescriptionKey,
      };
      const customSettings = {
        [PaymentType.CARD]: {
          isFree: false,
          descriptionKey: 'withdrawal.descriptions.card' satisfies DescriptionKey,
        },
        [PaymentType.PAPER_CHECK]: {
          isFree: false,
          descriptionKey: 'withdrawal.descriptions.paperCheck' satisfies DescriptionKey,
        },
      };

      const { isFree, descriptionKey } = customSettings[type] || defaultSetting;
      const fee = getFee(type);

      return {
        isFree,
        description: fee ? t(descriptionKey, { fee }) : undefined,
      };
    },
    [getFee, t]
  );

  const paymentTypesSettings: PaymentTypesSettingsMap = useMemo(
    () =>
      Object.values(PaymentType).reduce((settings, type) => {
        settings[type] = createPaymentTypeSetting(type);
        return settings;
      }, {} as PaymentTypesSettingsMap),
    [createPaymentTypeSetting]
  );

  const currentPaymentSettings = useMemo(() => {
    function applyFeatureFlagsAndFilters(type: PaymentType) {
      return (
        // WHOP_CRYPTO, WIRE and EDGE_BOOST are deposit-only, even if the backend returns such a withdrawal row
        !DEPOSIT_ONLY_PAYMENT_TYPES.includes(type) &&
        (showVIPPAndCheck || ![PaymentType.VIPPREFERRED, PaymentType.PAPER_CHECK].includes(type)) &&
        (showVenmo || type !== PaymentType.VENMO)
      );
    }

    const updatedSettings = {};

    if (isLoadingAvailablePaymentMethods) {
      return updatedSettings;
    }

    Object.entries(paymentTypesSettings).forEach(([type, settings]) => {
      if (applyFeatureFlagsAndFilters(type as PaymentType)) {
        if (isPaymentMethodDisplayed(type as PaymentMethod)) {
          updatedSettings[type as PaymentType] = {
            ...settings,
            isFree: !(
              withdrawalProcessingFees?.find(
                (fee) => fee.paymentMethod === PAYMENT_TYPE_TO_METHOD[type]
              )?.amount > 0
            ),
          };
        }
      }
    });

    return updatedSettings;
  }, [
    isLoadingAvailablePaymentMethods,
    isPaymentMethodDisplayed,
    paymentTypesSettings,
    showVIPPAndCheck,
    showVenmo,
    withdrawalProcessingFees,
  ]);

  const currentPaymentTypes = Object.entries(currentPaymentSettings) as PaymentTypes;

  const freePaymentTypes = currentPaymentTypes.reduce((acc, item) => {
    const [type, settings] = item;

    if (settings.isFree) {
      if (type === PaymentType.VIPPREFERRED) {
        acc.unshift(item);
      } else {
        acc.push(item);
      }
    }
    return acc;
  }, []);

  const paidPaymentTypes = currentPaymentTypes.filter(([_, settings]) => !settings.isFree);

  const isCurrentPaymentTypeDisabled = availablePaymentMethods?.some(
    (item) =>
      item.transactionType === 'withdrawal' &&
      item.paymentMethod === PAYMENT_TYPE_TO_METHOD[paymentType] &&
      item.status === 'disabled'
  );

  const getPaymentTypeDescription = () => {
    const paymentTypeSettings = paymentTypesSettings[paymentType];
    return paymentTypeSettings && 'description' in paymentTypeSettings
      ? paymentTypeSettings.description
      : undefined;
  };

  return {
    freePaymentTypes,
    paidPaymentTypes,
    paymentDescription: getPaymentTypeDescription(),
    isCurrentPaymentTypeDisabled,
    isFetchingAvailablePaymentMethods: isLoadingAvailablePaymentMethods || isProcessingFeesLoading,
  };
}
